'use client';

import { useMemo } from 'react';
import { getAPIToken } from '@instamenu/context-firebase';
import { UrqlProvider, ssrExchange, cacheExchange, fetchExchange, createClient } from '@urql/next';
import { filter, fromPromise, merge, mergeMap, pipe, share } from 'wonka';
import { makeErrorResult, makeOperation } from '@urql/core';
import { Client, Exchange, Operation, OperationResult } from 'urql';

type HeadersWithAuth = {
  headers?: {
    Authorization?: string | undefined;
  };
};

const addAuthHeader = async (fetchOptions: RequestInit & HeadersWithAuth) => {
  const token = await getAPIToken();
  const options = {
    ...fetchOptions,
    headers: {
      ...fetchOptions.headers,
    } as Record<string, string>,
  };
  if (token) options.headers.Authorization = `Bearer ${token}`;
  return options;
};

// A failed token refresh (e.g. auth/network-request-failed) must resolve the
// operation with a network error instead of leaving it pending forever.
export const firebaseAuthExchange: Exchange =
  ({ forward }) =>
  (operations$) => {
    const authResults$ = pipe(
      operations$,
      mergeMap((operation) => {
        const currentOptions =
          typeof operation.context.fetchOptions === 'function'
            ? operation.context.fetchOptions()
            : operation.context.fetchOptions || {};
        return fromPromise(
          addAuthHeader(currentOptions).then(
            (fetchOptions): Operation | OperationResult =>
              makeOperation(operation.kind, operation, {
                ...operation.context,
                fetchOptions,
              }),
            (error): Operation | OperationResult =>
              operation.kind === 'teardown' ? operation : makeErrorResult(operation, error),
          ),
        );
      }),
      share,
    );

    const forward$ = pipe(
      authResults$,
      filter((authResult): authResult is Operation => 'kind' in authResult),
      forward,
    );

    const error$ = pipe(
      authResults$,
      filter((authResult): authResult is OperationResult => !('kind' in authResult)),
    );

    return merge([forward$, error$]);
  };

interface InstamenuAPIProviderProps {
  children: React.ReactNode;
  endpointOverride?: string;
  host?: string;
}

let client: Client;
export const apiClient = () => client;

// The `ssrExchange` must be initialized with `isClient` and `initialState`
export const InstamenuAPIProvider = ({
  children,
  endpointOverride = '/graphql',
  host = process.env.NEXT_PUBLIC_API_ENDPOINT,
}: InstamenuAPIProviderProps) => {
  const [stateClient, ssr] = useMemo(() => {
    const ssr = ssrExchange();
    client = createClient({
      url: `${host}${endpointOverride}`,
      exchanges: [cacheExchange, ssr, firebaseAuthExchange, fetchExchange],
      fetchOptions: { cache: 'no-store' },
      requestPolicy: 'network-only',
    });
    return [client, ssr];
  }, [endpointOverride, host]);

  return (
    <UrqlProvider client={stateClient} ssr={ssr}>
      {children}
    </UrqlProvider>
  );
};
